import {useEffect, useRef} from 'preact/hooks';
import {useAuth} from './AuthProvider.jsx';
import {usePageContext} from '../hooks/usePageContext.js';

const SSO_DOMAINS = [
    {host: 'nightcore.com', origin: 'https://www.nightcore.com'},
    {host: 'lofitune.com', origin: 'https://www.lofitune.com'},
    {host: 'reverbslowed.com', origin: 'https://www.reverbslowed.com'},
];

const TRUSTED_ORIGINS = SSO_DOMAINS.flatMap(d => [d.origin, d.origin.replace('www.', '')]);

export function SSOSync() {
    const {user, authLoading} = useAuth();
    const iframeRefs = useRef([]);
    const readySet = useRef(new Set());
    const pendingMsg = useRef(null);
    const initialized = useRef(false);

    const pageContext = usePageContext();
    // Must resolve identically server- and client-side or the initial iframe
    // count (targets.length) will mismatch during hydration.
    const hostname = typeof window !== 'undefined'
        ? window.location.hostname
        : (pageContext?.headers?.host || '').split(':')[0];
    const isOnDomain = (host) => hostname === host || hostname.endsWith(`.${host}`);
    const targets = SSO_DOMAINS.filter(d => !isOnDomain(d.host));
    const sendToAll = (msg) => {
        pendingMsg.current = msg;
        iframeRefs.current.forEach((iframe, i) => {
            if (iframe && readySet.current.has(i)) {
                try {
                    iframe.contentWindow.postMessage(msg, targets[i].origin);
                } catch (_) {}
            }
        });
    };

    useEffect(() => {
        const onMessage = (event) => {
            if (!TRUSTED_ORIGINS.includes(event.origin)) return;
            if (event.data?.type !== 'SSO_READY') return;

            const idx = iframeRefs.current.findIndex(
                iframe => iframe && iframe.contentWindow === event.source
            );
            if (idx === -1) return;

            readySet.current.add(idx);

            if (pendingMsg.current) {
                try {
                    iframeRefs.current[idx].contentWindow.postMessage(pendingMsg.current, targets[idx].origin);
                } catch (_) {}
            }
        };

        window.addEventListener('message', onMessage);
        return () => window.removeEventListener('message', onMessage);
    }, []);

    useEffect(() => {
        if (authLoading) return;

        const apiKey = import.meta.env.VITE_FIREBASE_API_KEY;

        if (!initialized.current) {
            initialized.current = true;
            // On initial load, only sync if already logged in (avoid clearing other domains unnecessarily).
            if (user) sendToAll({type: 'SSO_LOGIN', userData: user.toJSON(), apiKey});
            return;
        }

        // Auth state changed after initial determination.
        if (user) {
            sendToAll({type: 'SSO_LOGIN', userData: user.toJSON(), apiKey});
        } else {
            sendToAll({type: 'SSO_LOGOUT', apiKey});
        }
    }, [user, authLoading]);

    if (targets.length === 0) return null;

    return (
        <div aria-hidden="true" style={{display: 'none'}}>
            {targets.map((domain, i) => (
                <iframe
                    key={domain.host}
                    src={`${domain.origin}/sso.html`}
                    ref={el => {iframeRefs.current[i] = el;}}
                    title="sso"
                />
            ))}
        </div>
    );
}
